import { useIsPreviewOrDemo } from "../../contexts/IsPreviewOrDemoContext";
import { useIsClickToEditEnabled } from "../../contexts/StaticSiteDataContext";
import { useCurrentSite } from "../../contexts/models/SiteContext";
import { TRACKING } from "../../core/components/LaunchDarklyProvider";
import {
  CashRegistryDetails,
  Configuration,
  OnlineInvitationSiteDetails,
  Section,
  SectionTypes,
  Site,
  WebsiteEvent,
} from "../../resources/modelTypes";
import { Page } from "../../resources/modelTypes";
import { SelectedCollection, SelectedRecord } from "../../resources/selectors";
import SitePreviewContext from "../../website/components/SitePreviewContext";
import { sectionTypeToSwipePanelView } from "../../website/components/online-invitation/mobile/SectionHeights";
import { sortRecordsByCreatedAtAndPosition } from "../../website/utils";
import { SiteDetailsSections } from "../collapsed/CollapsedSiteDetails";
import { selectors } from "../cypressSelectors";
import { PageSlugs } from "../types";
import { isEventDetailsEmpty } from "../utils";
import { DisplayEvent } from "./DisplayEvent";
import { DisplayRSVP } from "./DisplayRSVP";
import { DisplaySectionWebsite } from "./DisplaySection";
import { DisplaySiteDetails } from "./DisplaySiteDetails";
import { DisplayWrapper } from "./DisplayWrapper";
import {
  AttendantSection,
  DisplayAttendantSections,
} from "./section-types/DisplayAttendantSection";
import { DisplayDividerSection } from "./section-types/DisplayDividerSection";
import { DisplayMap } from "./section-types/DisplayMap";
import RegistriesGrid, {
  GroupedRegistriesSection,
} from "./section-types/RegistriesGrid";
import { css, SerializedStyles } from "@emotion/react";
import { MediumHeadline } from "@minted/fancyclothes";
import {
  PillButton,
  styleUtils,
  theme,
  useMediaQueryState,
} from "@minted/minted-components";
import React, { Fragment, memo, useCallback, useContext, useMemo } from "react";

export const dashedBorderStyle = css`
  border: ${styleUtils.rem(2)} dashed ${theme.colors.white};
  position: relative;

  &::before {
    border: ${styleUtils.rem(2)} dashed ${theme.colors.black};
    bottom: -${styleUtils.rem(4)};
    content: "";
    left: -${styleUtils.rem(4)};
    pointer-events: none;
    position: absolute;
    right: -${styleUtils.rem(4)};
    top: -${styleUtils.rem(4)};
  }
`;

export const displayWebsiteContentStyles = {
  addNewSectionContainer: css`
    align-items: center;
    background-color: rgba(255, 255, 255, 0.51);
    display: flex;
    justify-content: center;
    margin-top: ${theme.spacing.x10};
    padding: ${theme.spacing.x10};
  `,
  focusedSectionSolid: css`
    border: ${styleUtils.rem(2)} solid ${theme.colors.white};
    position: relative;

    &::before {
      border: ${styleUtils.rem(2)} solid ${theme.colors.borderFocused};
      bottom: -${styleUtils.rem(4)};
      content: "";
      left: -${styleUtils.rem(4)};
      pointer-events: none;
      position: absolute;
      right: -${styleUtils.rem(4)};
      top: -${styleUtils.rem(4)};
    }
  `,
  oiEventDetailsHeader: css`
    display: grid;
    justify-content: center;
    margin-bottom: ${theme.spacing.x4};
    text-align: center;
  `,
  oiRegistryHeader: css`
    margin-top: ${theme.spacing.x4};
  `,
  pillButton: css`
    display: flex;
    justify-content: center;
    margin-bottom: ${theme.spacing.x2};
  `,
  topPadding: css`
    padding-top: ${theme.spacing.x2};
  `,
};

export type DisplayWebsiteContentProps = {
  configuration: Configuration;
  eventCollection: SelectedCollection<WebsiteEvent> | null;
  page: Page;
  sectionCollection: SelectedCollection<Section>;
  site: Site;
};

type Sections = (SelectedRecord<WebsiteEvent> | SelectedRecord<Section>)[];

function getItemsWithGroupedRegistries(items: Sections) {
  let registryGroup: Sections = [];

  const itemsWithGroupedRegistries = items.reduce(
    (
      nextItems: Sections,
      section: SelectedRecord<WebsiteEvent> | SelectedRecord<Section>,
      index: number,
      arr: Sections,
    ): Sections => {
      const sectionData = section.data as Section;
      const sectionType: SectionTypes = sectionData.sectionType;
      const isLastItem = index === arr.length - 1;
      const isBirdieCashRegistry =
        sectionType === SectionTypes.CashRegistry &&
        Boolean(
          (sectionData.sectionDetails as CashRegistryDetails)?.cashRegistryId,
        );

      if (
        !isBirdieCashRegistry &&
        (sectionType === SectionTypes.Registry ||
          sectionType === SectionTypes.CashRegistry ||
          sectionType === SectionTypes.StripeRegistry)
      ) {
        registryGroup = [...registryGroup, section as SelectedRecord<Section>];

        if (isLastItem) {
          return [
            ...nextItems,
            {
              data: {
                sectionType: SectionTypes.RegistryGroup,
                registries: registryGroup,
              },
            } as unknown as SelectedRecord<Section>,
          ];
        }

        return [...nextItems];
      }

      const items = [
        ...(nextItems as Sections),
        ...(registryGroup.length > 0
          ? [
              {
                data: {
                  sectionType: SectionTypes.RegistryGroup,
                  registries: registryGroup,
                },
              } as unknown as SelectedRecord<Section>,
            ]
          : []),
        section as SelectedRecord<WebsiteEvent> | SelectedRecord<Section>,
      ];

      registryGroup = [];

      return items;
    },
    [] as (SelectedRecord<WebsiteEvent> | SelectedRecord<Section>)[],
  );

  return itemsWithGroupedRegistries;
}

const DisplayWebsiteContent = ({
  configuration,
  eventCollection,
  page,
  sectionCollection,
  site,
}: DisplayWebsiteContentProps) => {
  const { isPreview } = useIsPreviewOrDemo();
  const currentSite = useCurrentSite();
  const { isOnlineInvitation, isWedding } = currentSite;
  const isClickToEditEnabled = useIsClickToEditEnabled();

  const stagedSection = sectionCollection.resource.staged?.create?.[0];
  const stagedPageId = stagedSection?.data?.page;
  const sectionCollectionPageId = sectionCollection.params.page;
  const stagedEvent = eventCollection?.resource.staged?.create[0];
  const eventPageName = eventCollection?.resource?.name;
  const hasStagedCreation = stagedSection || stagedEvent;
  const {
    focusedClickSection,
    hoveredSection,
    onSectionEditClick,
    setFocusedClickSection,
    setHoveredSection,
    triggerAddNewSection,
  } = useContext(SitePreviewContext);

  const smallMediaQueryState = useMediaQueryState({
    mediaQuerySize: "small",
  });
  const isDesktop = smallMediaQueryState !== "BELOW";

  const isPreviewClickable = useMemo(
    () => isPreview && isClickToEditEnabled,
    [isPreview, isClickToEditEnabled],
  );

  // Handles section "focus" and on double click (or initial click with skipStage) opens resource editor
  const handleSectionClick = useCallback(
    (
      idOfResource: number | SiteDetailsSections,
      resourceType: "event" | "section" | "siteDetails",
      sectionType: SectionTypes,
      skipStage: boolean = false,
    ) => {
      const isAlreadyStaged =
        focusedClickSection?.id === idOfResource &&
        focusedClickSection?.type === resourceType;

      if (isAlreadyStaged || skipStage) {
        setFocusedClickSection({
          id: idOfResource,
          isSolidBorder: true,
          type: resourceType,
        });

        const panelView = sectionTypeToSwipePanelView(sectionType);
        // Pass the page so the sidebar knows which page context we're in
        onSectionEditClick(idOfResource, resourceType, panelView, page);
      } else {
        setFocusedClickSection({
          id: idOfResource,
          isSolidBorder: false,
          type: resourceType,
        });
      }
    },
    [onSectionEditClick, focusedClickSection, page, setFocusedClickSection],
  );

  const allItems = useMemo(() => {
    let items: (SelectedRecord<WebsiteEvent> | SelectedRecord<Section>)[];

    if (eventCollection?.items) {
      items = [...eventCollection.items, ...sectionCollection.items];

      if (stagedEvent && eventPageName === PageSlugs.Events) {
        items.push(stagedEvent);
      }
    } else {
      items = sectionCollection.items;
    }

    if (stagedSection && stagedPageId === sectionCollectionPageId) {
      items.push(stagedSection);
    }

    sortRecordsByCreatedAtAndPosition(items);

    return getItemsWithGroupedRegistries(items);
  }, [
    eventCollection,
    sectionCollection.items,
    stagedEvent,
    eventPageName,
    stagedSection,
    stagedPageId,
    sectionCollectionPageId,
  ]);

  const extraComponentsForSlug = useMemo(() => {
    if (page.slug === PageSlugs.RSVP) {
      return <DisplayRSVP configuration={configuration} />;
    }

    return null;
  }, [configuration, page.slug]);

  const sectionElements: React.ReactNode[] = useMemo(() => {
    const elements: React.ReactNode[] = [];

    // Render site details for OI sites at the top
    if (isOnlineInvitation && site.siteDetails) {
      const siteDetails =
        site.siteDetails as unknown as OnlineInvitationSiteDetails;
      const isSiteDetailsFocused =
        focusedClickSection?.id ===
          SiteDetailsSections.EventNameAndDescription &&
        focusedClickSection?.type === "siteDetails";

      const isSiteDetailsHovered =
        hoveredSection?.id === SiteDetailsSections.EventNameAndDescription &&
        hoveredSection?.type === "siteDetails";

      let siteDetailsBorderStyle: SerializedStyles | undefined;
      let siteDetailsClickHandler: (() => void) | undefined;

      // Assign relevant styling/handler if new preview experience is enabled
      if (isPreview) {
        if (isSiteDetailsFocused) {
          siteDetailsBorderStyle = focusedClickSection.isSolidBorder
            ? displayWebsiteContentStyles.focusedSectionSolid
            : dashedBorderStyle;
        } else if (isSiteDetailsHovered && isPreviewClickable) {
          siteDetailsBorderStyle = dashedBorderStyle;
        }

        if (!hasStagedCreation && isPreviewClickable) {
          siteDetailsClickHandler = () =>
            handleSectionClick(
              SiteDetailsSections.EventNameAndDescription,
              "siteDetails",
              SectionTypes.Text,
              isDesktop,
            );
        }
      }

      // Display PillButton when on initial hover/focus. Hide if editing
      const shouldDisplayPillButton =
        isPreview &&
        !(isSiteDetailsFocused && focusedClickSection?.isSolidBorder) &&
        (isSiteDetailsFocused || (isSiteDetailsHovered && isPreviewClickable));

      elements.push(
        <Fragment key="siteDetails">
          <div
            onMouseEnter={
              isPreviewClickable
                ? () =>
                    setHoveredSection({
                      id: SiteDetailsSections.EventNameAndDescription,
                      type: "siteDetails",
                    })
                : undefined
            }
            onMouseLeave={
              isPreviewClickable ? () => setHoveredSection(null) : undefined
            }
          >
            {shouldDisplayPillButton && (
              <div css={displayWebsiteContentStyles.pillButton}>
                <PillButton
                  iconAlignment="right"
                  iconType="editSquare"
                  onClick={siteDetailsClickHandler}
                  size="small"
                />
              </div>
            )}
            <div
              css={[
                displayWebsiteContentStyles.topPadding,
                siteDetailsBorderStyle,
              ]}
              data-cy={`${selectors.website_displaySection}_siteDetails`}
              onClick={siteDetailsClickHandler}
            >
              <DisplaySiteDetails siteDetails={siteDetails} />
            </div>
          </div>
        </Fragment>,
      );
    }

    for (let index = 0; index < allItems.length; index++) {
      const current = allItems[index] as
        | SelectedRecord<WebsiteEvent>
        | SelectedRecord<Section>;
      const isEvent = !("sectionType" in current.data);

      if (isEvent) {
        const event = current.data as WebsiteEvent;

        if (event.private) {
          continue;
        }

        const isEventEditFocused =
          focusedClickSection?.id === event.id &&
          focusedClickSection?.type === "event";

        const isEventHovered =
          hoveredSection?.id === event.id && hoveredSection?.type === "event";

        let eventBorderStyle: null | SerializedStyles = null;

        if (isEventEditFocused) {
          if (focusedClickSection.isSolidBorder) {
            eventBorderStyle = displayWebsiteContentStyles.focusedSectionSolid;
          } else {
            eventBorderStyle = dashedBorderStyle;
          }
        } else if (
          isEventDetailsEmpty(event) ||
          (isEventHovered && isPreviewClickable)
        ) {
          eventBorderStyle = dashedBorderStyle;
        }

        // Only display PillButton on initial hover/focus, or if the Event Details are empty
        const shouldDisplayPillButton =
          isPreview &&
          !(isEventEditFocused && focusedClickSection?.isSolidBorder) &&
          (isEventEditFocused ||
            isEventDetailsEmpty(event) ||
            (isEventHovered && isPreviewClickable));

        const eventClickHandler =
          isPreview && !hasStagedCreation && isPreviewClickable
            ? () =>
                handleSectionClick(
                  event.id,
                  "event",
                  SectionTypes.Event__FAKE,
                  isEventDetailsEmpty(event) || isPreviewClickable,
                )
            : undefined;

        elements.push(
          <Fragment key={`collapsed${event.id}`}>
            <div
              onMouseEnter={
                isPreviewClickable
                  ? () => setHoveredSection({ id: event.id, type: "event" })
                  : undefined
              }
              onMouseLeave={
                isPreviewClickable ? () => setHoveredSection(null) : undefined
              }
            >
              {shouldDisplayPillButton && (
                <div css={displayWebsiteContentStyles.pillButton}>
                  <PillButton
                    iconAlignment="right"
                    iconType="editSquare"
                    onClick={eventClickHandler}
                    size="small"
                  />
                </div>
              )}
              <div
                css={isPreview && eventBorderStyle}
                data-cy={`${selectors.website_displaySection}_event`}
                id={`section-${event.id || "new"}`}
                onClick={eventClickHandler}
              >
                {isOnlineInvitation && (
                  <div css={displayWebsiteContentStyles.oiEventDetailsHeader}>
                    <MediumHeadline>Event Details</MediumHeadline>
                  </div>
                )}
                <DisplayEvent configuration={configuration} event={event} />
                {configuration.showPageDividers && (
                  <DisplayDividerSection
                    hideLast={index === allItems.length - 1}
                  />
                )}
              </div>
            </div>
          </Fragment>,
        );
        continue;
      }

      const section = current.data as Section;

      if (section.sectionType === SectionTypes.Attendant) {
        const attendantGroup = [section] as AttendantSection[];

        const nextSection = allItems[index + 1]?.data as AttendantSection;

        // If next section is also an Attendant, group them for potential grid display
        if (nextSection && nextSection.sectionType === SectionTypes.Attendant) {
          attendantGroup.push(nextSection);
          index++;
        }

        elements.push(
          <DisplayAttendantSections
            configuration={configuration}
            page={page}
            sections={attendantGroup}
            classes={{
              dashedBorderStyle,
              focusedSectionSolid:
                displayWebsiteContentStyles.focusedSectionSolid,
            }}
          />,
        );
        continue;
      }

      const shouldDisplayDivider =
        configuration.showPageDividers &&
        [
          SectionTypes.Accommodation,
          SectionTypes.Registry,
          SectionTypes.CashRegistry,
        ].includes(section.sectionType);

      const isSectionFocused =
        focusedClickSection?.id === section.id &&
        focusedClickSection?.type === "section";

      const isSectionHovered =
        hoveredSection?.id === section.id && hoveredSection?.type === "section";

      let sectionBorderStyle: SerializedStyles | undefined;
      let sectionClickHandler: (() => void) | undefined;

      // Assign relevant styling/handler if new preview experience is enabled
      if (
        isPreview &&
        section.sectionType !== SectionTypes.Comments &&
        section.sectionType !== SectionTypes.Divider
      ) {
        if (section.id === undefined) {
          sectionBorderStyle = displayWebsiteContentStyles.focusedSectionSolid;
        } else if (isSectionFocused) {
          sectionBorderStyle = focusedClickSection.isSolidBorder
            ? displayWebsiteContentStyles.focusedSectionSolid
            : dashedBorderStyle;
        } else if (isSectionHovered && isPreviewClickable) {
          sectionBorderStyle = dashedBorderStyle;
        }

        if (!hasStagedCreation && isPreviewClickable) {
          sectionClickHandler = () =>
            handleSectionClick(
              section.id,
              "section",
              section.sectionType,
              isPreviewClickable,
            );
        }
      }

      // Display PillButton when on initial hover/focus. Hide if editing
      const shouldDisplayPillButton =
        isPreview &&
        !(isSectionFocused && focusedClickSection?.isSolidBorder) &&
        (isSectionFocused || (isSectionHovered && isPreviewClickable));

      if (section.sectionType === SectionTypes.RegistryGroup) {
        elements.push(
          <RegistriesGrid
            configuration={configuration}
            section={section as GroupedRegistriesSection}
          />,
        );
        continue;
      }

      elements.push(
        <Fragment key={`collapsed${section.id}`}>
          <div
            onMouseEnter={
              isPreviewClickable &&
              section.sectionType !== SectionTypes.Comments &&
              section.sectionType !== SectionTypes.Divider
                ? () => setHoveredSection({ id: section.id, type: "section" })
                : undefined
            }
            onMouseLeave={
              isPreviewClickable &&
              section.sectionType !== SectionTypes.Comments &&
              section.sectionType !== SectionTypes.Divider
                ? () => setHoveredSection(null)
                : undefined
            }
          >
            {shouldDisplayPillButton &&
              section.sectionType !== SectionTypes.Divider && (
                <div css={displayWebsiteContentStyles.pillButton}>
                  <PillButton
                    iconAlignment="right"
                    iconType="editSquare"
                    onClick={sectionClickHandler}
                    size="small"
                  />
                </div>
              )}
            <div
              css={sectionBorderStyle}
              data-cy={`${selectors.website_displaySection}_${section.sectionType}`}
              id={`section-${section.id || "new"}`}
              onClick={sectionClickHandler}
            >
              <DisplaySectionWebsite
                configuration={configuration}
                section={section}
              />
            </div>
          </div>
          {shouldDisplayDivider && <DisplayDividerSection hideLast />}
        </Fragment>,
      );
    }

    if (isPreview && isPreviewClickable) {
      elements.push(
        <div
          css={[
            displayWebsiteContentStyles.addNewSectionContainer,
            dashedBorderStyle,
          ]}
          key="add-new-section"
        >
          <PillButton
            iconAlignment="right"
            iconType="add"
            onClick={() => {
              triggerAddNewSection(
                TRACKING.PreviewSectionAddElementClicked,
                page,
                isWedding,
              );
            }}
            size="small"
            text="Add New Section"
          />
        </div>,
      );
    }

    return elements;
  }, [
    allItems,
    configuration,
    focusedClickSection,
    handleSectionClick,
    hasStagedCreation,
    hoveredSection,
    isDesktop,
    isPreviewClickable,
    isOnlineInvitation,
    isPreview,
    isWedding,
    page,
    setHoveredSection,
    site,
    triggerAddNewSection,
  ]);

  return (
    <DisplayWrapper
      isMobile={!isDesktop}
      textAlignment={configuration.textAlignment}
    >
      {extraComponentsForSlug}
      {sectionElements}
      {isWedding &&
        page.slug === PageSlugs.Events &&
        configuration.showMap &&
        eventCollection && <DisplayMap events={eventCollection.items} />}
    </DisplayWrapper>
  );
};

const MemoizedDisplayWebsiteContent = memo(DisplayWebsiteContent);

export { MemoizedDisplayWebsiteContent as DisplayWebsiteContent };
